import { Button, Loader, Modal } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { prvApi } from "#/api/prvApi";
import { type PrvRun, PrvRunStatus } from "#/types/prv";

interface Props {
  clientId: number;
  /** The run being followed; null keeps the modal closed. */
  runId: number | null;
  /** Called exactly once when the run reaches `completed`. */
  onCompleted: (run: PrvRun) => void;
  /** Called when the admin dismisses a failed run. */
  onDismissFailure: () => void;
}

/**
 * Small centered progress window. Polls the run every second, shows the
 * engine's live step, and hands over when the run completes. Cannot be
 * dismissed while running: the run continues server-side either way, and
 * reopens on reload via the tab's active-run check.
 */
const PrvRunModal = ({ clientId, runId, onCompleted, onDismissFailure }: Props) => {
  const { t } = useTranslation();
  const completedFor = useRef<number | null>(null);

  const { data: run } = useQuery({
    queryKey: ["prv-run", clientId, runId],
    queryFn: async () => (await prvApi.getRun(clientId, runId!)).data,
    enabled: runId !== null,
    refetchInterval: (query) =>
      query.state.data?.status === PrvRunStatus.RUNNING || !query.state.data ? 1000 : false,
  });

  useEffect(() => {
    if (run && run.status === PrvRunStatus.COMPLETED && completedFor.current !== run.id) {
      completedFor.current = run.id;
      onCompleted(run);
    }
  }, [run, onCompleted]);

  const failed = run?.status === PrvRunStatus.FAILED;
  const stepText = run?.step
    ? t(`clients.prv.modal.steps.${run.step}`)
    : t("clients.prv.modal.starting");

  return (
    <Modal
      opened={runId !== null}
      onClose={() => {
        if (failed) onDismissFailure();
      }}
      centered
      size="sm"
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={failed}
      overlayProps={{ blur: 4, backgroundOpacity: 0.35 }}
      title={t("clients.prv.modal.title")}
    >
      {failed ? (
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3">
            <IconAlertTriangle size={20} className="mt-0.5 shrink-0 text-red-600" />
            <div className="flex flex-col gap-1">
              <span className="text-sm font-semibold text-red-800">
                {t("clients.prv.modal.failedTitle")}
              </span>
              <span className="text-sm text-red-700">{run?.error ?? "--"}</span>
            </div>
          </div>
          <div className="flex justify-end">
            <Button variant="outline" color="red" onClick={onDismissFailure}>
              {t("common.close")}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-5 py-2">
          <Loader color="var(--client-color)" size="lg" />
          <div className="flex flex-col items-center gap-1 text-center">
            <span className="text-sm font-medium text-gray-900" aria-live="polite">
              {stepText}
            </span>
            {run && run.rowsRead > 0 && (
              <span className="text-xs text-gray-500">
                {t("clients.prv.runLog.rowsRead")}: {run.rowsRead}
              </span>
            )}
          </div>
          <p className="border-t border-gray-100 pt-4 text-center text-xs text-gray-500">
            {t("clients.prv.modal.waitNote")}
          </p>
        </div>
      )}
    </Modal>
  );
};

export default PrvRunModal;
